import { useEffect, useState, useRef } from 'react';
import { orderApi } from '@formosa/shared/api/order';
import { formatApiDateTime } from '@formosa/shared/utils/dateTime';
import { auditBars, diffBarAudits, isChartDebugEnabled } from '@formosa/shared/utils/chartDebug';
import { mergeBarList, subscribeFuturesChart, dedupeBars } from '@formosa/shared/socket/futuresSocket';

/** 歷史 K 線預設回溯時數（約 26 小時） */
const DEFAULT_HISTORICAL_HOURS = 26;

/**
 * 交割 K 線：先 REST 歷史區間繪製，再 Socket 推送最新 K 線。
 */
export default function useFuturesChartSocket(productId, symbolCode, options = {}) {
  const {
    maxBars = 1600,
    historicalHours = DEFAULT_HISTORICAL_HOURS,
    enabled = true,
  } = options;
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(false);
  const rawApiBarsRef = useRef([]);

  useEffect(() => {
    if (isChartDebugEnabled()) {
      console.info('[ChartDebug] useFuturesChartSocket 啟動', { productId, symbolCode });
    }
    if (!enabled || !productId || !symbolCode) {
      setBars([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = () => {};

    async function loadHistorical() {
      setLoading(true);
      try {
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - historicalHours * 60 * 60 * 1000);
        const rows = await orderApi.getFuturesChart(productId, {
          startTime: formatApiDateTime(startTime),
          endTime: formatApiDateTime(endTime),
        });
        const rawList = Array.isArray(rows) ? rows : [];
        rawApiBarsRef.current = rawList;
        auditBars(rawList, 'raw api', { productId, symbolCode, source: 'GET /futures/chart' });
        if (!cancelled) {
          const deduped = dedupeBars(rawList, maxBars);
          auditBars(deduped, 'chart data after dedupe', { productId, symbolCode });
          diffBarAudits(rawList, deduped, 'raw api', 'chart data after dedupe');
          setBars(deduped);
        }
      } catch {
        if (!cancelled) {
          setBars([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    async function init() {
      await loadHistorical();
      if (cancelled) {
        return;
      }

      unsubscribe = subscribeFuturesChart(productId, {
        onSnapshot: (snapshotBars) => {
          if (cancelled) {
            return;
          }
          const snapshotList = Array.isArray(snapshotBars) ? snapshotBars : [];
          auditBars(snapshotList, 'socket chartSnapshot', { productId, symbolCode });
          setBars((prev) => {
            const snapshot = dedupeBars(snapshotList, maxBars);
            if (prev.length === 0) {
              auditBars(snapshot, 'chart data after snapshot (prev empty)', { productId, symbolCode });
              diffBarAudits(rawApiBarsRef.current, snapshot, 'raw api', 'chart data after snapshot');
              return snapshot;
            }
            const merged = dedupeBars([...prev, ...snapshot], maxBars);
            auditBars(merged, 'chart data after snapshot merge', { productId, symbolCode, prevCount: prev.length });
            diffBarAudits(rawApiBarsRef.current, merged, 'raw api', 'chart data after snapshot merge');
            return merged;
          });
        },
        onUpdate: (bar) => {
          if (!cancelled) {
            auditBars(bar ? [bar] : [], 'socket chartUpdate (single bar)', { productId, symbolCode });
            setBars((prev) => {
              const merged = mergeBarList(prev, bar, maxBars);
              auditBars(merged, 'chart data after chartUpdate merge', {
                productId,
                symbolCode,
                prevCount: prev.length,
              });
              diffBarAudits(rawApiBarsRef.current, merged, 'raw api', 'chart data after chartUpdate merge');
              return merged;
            });
          }
        },
      });
    }

    init();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [productId, symbolCode, enabled, maxBars, historicalHours]);

  return { bars, loading };
}
